import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Download, Clock, HardDrive, FileText, Film, Image as ImageIcon,
  FileArchive, Music, File as FileIcon, ExternalLink, CheckCircle2,
  AlertCircle, ChevronLeft, ChevronRight, X, Play,
} from 'lucide-react';
import { brandFor } from '../lib/brands';
import FindMyPhotosBanner from '../components/FindMyPhotosBanner';


const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const isImage = (m) => (m || '').startsWith('image/');
const isVideo = (m) => (m || '').startsWith('video/');
const isAudio = (m) => (m || '').startsWith('audio/');
const isPreviewable = (m) => isImage(m) || isVideo(m);

const iconFor = (mime, name) => {
  if (isImage(mime)) return ImageIcon;
  if (isVideo(mime)) return Film;
  if (isAudio(mime)) return Music;
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return FileArchive;
  if (['pdf', 'txt', 'md', 'doc', 'docx'].includes(ext)) return FileText;
  return FileIcon;
};

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return useMemo(() => {
    if (!targetIso) return null;
    const diff = new Date(targetIso).getTime() - now;
    if (diff <= 0) return { label: 'Expired', danger: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    let label;
    if (days >= 1) label = `${days}d ${hours}h ${mins}m`;
    else if (hours >= 1) label = `${hours}h ${mins}m ${secs}s`;
    else label = `${mins}m ${secs}s`;
    return { label, danger: days < 1, warning: days === 1 };
  }, [targetIso, now]);
};

// Direct Drive URL builders — used by both thumbnails and the lightbox.
// `lh3.googleusercontent.com/d/{id}=w{size}` is the CDN that Drive uses under
// the hood for public files — MUCH more reliable than `drive.google.com/thumbnail`
// (which sometimes returns an empty/black image for freshly-shared files).
const driveThumbUrl = (fileId, size = 400) => `https://lh3.googleusercontent.com/d/${fileId}=w${size}`;
const driveFallbackThumbUrl = (fileId, size = 400) => `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
const driveFullImageUrl = (fileId) => `https://lh3.googleusercontent.com/d/${fileId}=w2400`;
// For video preview inside the lightbox — Drive supports a preview iframe.
const drivePreviewIframeUrl = (fileId) => `https://drive.google.com/file/d/${fileId}/preview`;

// Thumbnail component: tries lh3 CDN, then drive.google.com/thumbnail, then falls back to an icon.
const Thumb = ({ file, isLocal }) => {
  const [attempt, setAttempt] = useState(0); // 0 = lh3, 1 = drive.google.com, 2 = icon fallback
  const Icon = iconFor(file.mime_type, file.name);

  if (isImage(file.mime_type)) {
    if (isLocal && file.web_content_link) {
      return (
        <img src={file.web_content_link} alt={file.name} loading="lazy"
             className="w-full h-full object-cover"
             onError={() => setAttempt(2)} />
      );
    }
    if (file.drive_file_id && attempt < 2) {
      const src = attempt === 0 ? driveThumbUrl(file.drive_file_id, 400) : driveFallbackThumbUrl(file.drive_file_id, 400);
      return (
        <img src={src} alt={file.name} loading="lazy" referrerPolicy="no-referrer"
             className="w-full h-full object-cover"
             onError={() => setAttempt((a) => a + 1)} />
      );
    }
  }

  if (isVideo(file.mime_type) && file.drive_file_id && attempt < 2) {
    // Google generates JPEG thumbnails for videos too, at the same endpoints
    const src = attempt === 0 ? driveThumbUrl(file.drive_file_id, 400) : driveFallbackThumbUrl(file.drive_file_id, 400);
    return (
      <div className="relative w-full h-full">
        <img src={src} alt={file.name} loading="lazy" referrerPolicy="no-referrer"
             className="w-full h-full object-cover opacity-80"
             onError={() => setAttempt((a) => a + 1)} />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/60 border border-white/30 flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-white/60">
      <Icon className="w-8 h-8" />
      <span className="text-[10px] uppercase tracking-wider">{(file.name || '').split('.').pop().slice(0, 6)}</span>
    </div>
  );
};

// Lightbox: full-screen viewer for images/videos with next/prev and download.
const Lightbox = ({ files, startIndex, onClose, onDownload, isLocal, brand }) => {
  const [idx, setIdx] = useState(startIndex);
  const file = files[idx];

  const goPrev = useCallback(() => setIdx((i) => (i - 1 + files.length) % files.length), [files.length]);
  const goNext = useCallback(() => setIdx((i) => (i + 1) % files.length), [files.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  if (!file) return null;
  const canPrevNext = files.length > 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={onClose} data-testid="lightbox">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm text-white/80 truncate flex-1 min-w-0">
          <span className="text-white/50">{idx + 1} / {files.length}</span>
          <span className="mx-2 text-white/20">·</span>
          <span className="truncate" title={file.name}>{file.name}</span>
          <span className="ml-2 text-white/40 text-xs">{fmtSize(file.size)}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <button onClick={() => onDownload(file)}
            className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5"
            style={{ background: brand.accent, color: brand.accentText }}
            data-testid="lightbox-download">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="lightbox-close" aria-label="Close">
            <X className="w-5 h-5 text-white/80" />
          </button>
        </div>
      </div>

      {/* Viewer */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {canPrevNext && (
          <button onClick={goPrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 border border-white/20 flex items-center justify-center"
            data-testid="lightbox-prev" aria-label="Previous">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}
        {isImage(file.mime_type) ? (
          isLocal ? (
            <img src={file.web_content_link} alt={file.name} className="max-h-full max-w-full object-contain" />
          ) : (
            <img src={driveFullImageUrl(file.drive_file_id)} alt={file.name} className="max-h-full max-w-full object-contain" referrerPolicy="no-referrer" />
          )
        ) : isVideo(file.mime_type) ? (
          isLocal ? (
            /* Local videos: use a native <video> since we serve them directly.
               No <track> caption available; controls provide sufficient a11y. */
            <video src={file.web_content_link} controls autoPlay className="max-h-full max-w-full">
              <track kind="captions" />
            </video>
          ) : (
            /* Drive videos: use Google's own preview iframe — reliably handles all codecs */
            <iframe src={drivePreviewIframeUrl(file.drive_file_id)}
              className="w-full h-full max-w-5xl max-h-full border-0"
              allow="autoplay; fullscreen"
              title={file.name} />
          )
        ) : (
          <div className="text-white/70 text-center p-8">
            <FileIcon className="w-16 h-16 mx-auto mb-4 text-white/40" />
            <div>No preview available for this file type.</div>
            <div className="text-xs text-white/50 mt-1">Click Download above to get the file.</div>
          </div>
        )}
        {canPrevNext && (
          <button onClick={goNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 border border-white/20 flex items-center justify-center"
            data-testid="lightbox-next" aria-label="Next">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}
      </div>
    </div>
  );
};

const TransferDownloadPage = () => {
  const { token } = useParams();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadedIds, setDownloadedIds] = useState(new Set());
  const [downloadAllProgress, setDownloadAllProgress] = useState({ current: 0, total: 0 });
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Day-wise grouping for multi-day events: GET /api/transfers/public/:token/days → [{id, title, file_ids, status}]
  // (empty when the sender never added a second day → the classic flat grid).
  const [days, setDays] = useState([]);
  const [daysUploading, setDaysUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let pollHandle = null;

    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/transfers/public/${token}`);
        if (cancelled) return;
        setTransfer(res.data);
        // If the transfer isn't ready yet (uploads still in progress), keep
        // polling every 8 seconds so the page auto-refreshes as files land.
        // Stops as soon as `ready: true` arrives.
        if (res.data && res.data.ready === false) {
          pollHandle = setTimeout(load, 8000);
        }
        // Days (multi-day events). While a day is still uploading, keep refreshing every 15 s so new
        // files show up without a manual reload.
        try {
          const dr = await axios.get(`${BACKEND}/api/transfers/public/${token}/days`);
          if (cancelled) return;
          setDays(dr.data?.days || []);
          setDaysUploading(!!dr.data?.uploading);
          if (dr.data?.uploading && !pollHandle) pollHandle = setTimeout(load, 15000);
        } catch (_) {
          if (!cancelled) { setDays([]); setDaysUploading(false); }
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Transfer not found or expired');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (pollHandle) clearTimeout(pollHandle);
    };
  }, [token]);

  const countdown = useCountdown(transfer?.expires_at);
  const totalSize = useMemo(() => (transfer?.files || []).reduce((a, f) => a + (f.size || 0), 0), [transfer]);
  const isLocal = transfer?.account_key === 'local' || transfer?.account_key === '__local__';
  const brand = useMemo(() => brandFor(transfer?.account_key), [transfer]);

  const openDownload = (file) => {
    let href;
    if (isLocal) href = file.web_content_link;
    else href = `https://drive.google.com/uc?export=download&id=${file.drive_file_id}&confirm=t`;
    const a = document.createElement('a');
    a.href = href; a.rel = 'noopener noreferrer'; a.target = '_blank'; a.download = file.name || '';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setDownloadedIds(prev => new Set(prev).add(file.drive_file_id));
  };

  const downloadAll = async () => {
    if (!transfer?.files?.length || downloadingAll) return;
    setDownloadingAll(true);
    const total = transfer.files.length;
    setDownloadAllProgress({ current: 0, total });
    try {
      for (let i = 0; i < transfer.files.length; i++) {
        openDownload(transfer.files[i]);
        setDownloadAllProgress({ current: i + 1, total });
        if (i < transfer.files.length - 1) await new Promise(r => setTimeout(r, 900));
      }
      toast.success(`Started ${total} download${total === 1 ? '' : 's'}. Check your browser's downloads.`);
    } finally { setDownloadingAll(false); }
  };

  // Same as downloadAll, for one day's files only.
  const downloadDay = async (section) => {
    const list = section?.files || [];
    if (!list.length || downloadingAll) return;
    setDownloadingAll(true);
    setDownloadAllProgress({ current: 0, total: list.length });
    try {
      for (let i = 0; i < list.length; i++) {
        openDownload(list[i]);
        setDownloadAllProgress({ current: i + 1, total: list.length });
        if (i < list.length - 1) await new Promise(r => setTimeout(r, 900));
      }
      toast.success(`Started ${list.length} download${list.length === 1 ? '' : 's'} for ${section.title}.`);
    } finally { setDownloadingAll(false); }
  };

  const openDriveFolder = () => {
    if (transfer?.drive_folder_view_link) window.open(transfer.drive_folder_view_link, '_blank', 'noopener,noreferrer');
    else if (transfer?.drive_folder_id) window.open(`https://drive.google.com/drive/folders/${transfer.drive_folder_id}`, '_blank', 'noopener,noreferrer');
  };

  const openLightbox = (file, idx) => {
    if (isPreviewable(file.mime_type)) setLightboxIdx(idx);
    else openDownload(file);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white/60 text-sm">Loading…</div>;
  }
  if (error || !transfer) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Link unavailable</h1>
          <p className="text-white/60 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const files = transfer.files || [];
  // Group by day when the sender added more days to this link. Files are laid out day after day, so the
  // grid, the "Download Day N" buttons and the lightbox all walk through them in the same order.
  const grouped = days.length > 1;
  let daySections = [];
  let orderedFiles = files;
  if (grouped) {
    const byId = new Map(files.map((f) => [f.drive_file_id, f]));
    daySections = days.map((d) => ({ ...d, files: (d.file_ids || []).map((id) => byId.get(id)).filter(Boolean) }));
    const placed = new Set(daySections.flatMap((s) => s.files.map((f) => f.drive_file_id)));
    const rest = files.filter((f) => !placed.has(f.drive_file_id));
    if (rest.length) (daySections.find((s) => s.status === 'uploading') || daySections[daySections.length - 1]).files.push(...rest);
    orderedFiles = daySections.flatMap((s) => s.files);
  }
  const sectionStarts = new Map();
  let cursor = 0;
  daySections.forEach((s) => { if (s.files.length) sectionStarts.set(cursor, s); cursor += s.files.length; });
  const emptyDays = daySections.filter((s) => s.files.length === 0);
  const showFolderDownload = !isLocal && (transfer.drive_folder_view_link || transfer.drive_folder_id);
  // When the sender shared the link before uploads finished, `ready === false`
  // and we show a friendly preparing screen with any files that have already
  // landed. Auto-polls every 8s (set up in useEffect above) until ready.
  const preparing = transfer.ready === false;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-transfer">
      {/* Branded header */}
      <header className="border-b border-white/10 sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-8 h-8 rounded flex items-center justify-center font-bold text-sm shrink-0"
              style={{ background: brand.accent, color: brand.accentText }} data-testid="brand-mark">
              {brand.mark}
            </span>
            <span className="font-semibold tracking-tight truncate" data-testid="brand-name">{brand.name}</span>
            <span className="hidden sm:inline text-white/40 text-xs">· {brand.tagline}</span>
          </div>
          {countdown && (
            <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border shrink-0 ${
              countdown.danger ? 'border-rose-500/40 bg-rose-500/10 text-rose-300' :
              countdown.warning ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' :
              'border-white/15 bg-white/5 text-white/70'
            }`} data-testid="expiry-countdown">
              <Clock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Expires in </span>
              <span>{countdown.label}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-12">
        <div className="mb-6 sm:mb-8">
          <div className="text-xs uppercase tracking-wider mb-2" style={{ color: brand.accent }}>
            {transfer.uploader_name ? `From ${transfer.uploader_name}` : 'Shared with you'}
          </div>
          <h1 className="text-xl sm:text-4xl font-semibold tracking-tight text-white mb-3 break-words" data-testid="transfer-title">
            {transfer.title || 'Files shared with you'}
          </h1>
          {transfer.message && (
            <p className="text-white/70 leading-relaxed whitespace-pre-line max-w-2xl text-sm sm:text-base">{transfer.message}</p>
          )}
        </div>

        {preparing && (
          <div className="mb-6 p-4 rounded-lg border flex items-start gap-3"
            style={{ borderColor: brand.accentBorder, background: brand.accentSoft }}
            data-testid="preparing-banner">
            <Clock className="w-5 h-5 mt-0.5 shrink-0 animate-pulse" style={{ color: brand.accent }} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: brand.accent }}>
                Files are still being prepared
              </div>
              <div className="text-sm text-white/70 mt-1">
                {files.length > 0
                  ? `${files.length} file${files.length === 1 ? '' : 's'} ready so far — more will appear here as they upload. This page refreshes automatically every few seconds.`
                  : 'The sender started uploading — files will appear here as they finish. This page refreshes automatically every few seconds.'}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 text-xs sm:text-sm">
          <div className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10 flex items-center gap-2">
            <HardDrive className="w-3.5 h-3.5 text-white/50" />
            <span>{files.length} {files.length === 1 ? 'file' : 'files'}</span>
          </div>
          <div className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10 flex items-center gap-2">
            <span className="text-white/50">Total</span>
            <span className="font-medium">{fmtSize(transfer.total_size || totalSize)}</span>
          </div>
          {grouped && (
            <div className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10 flex items-center gap-2" data-testid="days-chip">
              <Clock className={`w-3.5 h-3.5 text-white/50 ${daysUploading ? 'animate-pulse' : ''}`} />
              <span>{days.length} days{daysUploading ? ' · more files coming' : ''}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
          <Button onClick={downloadAll} disabled={downloadingAll || files.length === 0}
            className="font-semibold h-12 flex-1 sm:flex-initial sm:min-w-[220px]"
            style={{ background: brand.accent, color: brand.accentText }}
            data-testid="download-all-btn">
            <Download className="w-5 h-5 mr-2" />
            {downloadingAll
              ? `Downloading ${downloadAllProgress.current}/${downloadAllProgress.total}…`
              : preparing
                ? `Download ${files.length} ready now`
                : `Download all (${files.length})`}
          </Button>
          {showFolderDownload && (
            <Button onClick={openDriveFolder} variant="outline"
              className="border-white/15 bg-white/5 hover:bg-white/10 h-12"
              data-testid="open-drive-folder-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Drive folder <span className="hidden sm:inline text-white/40 ml-1.5 text-xs">(ZIP)</span>
            </Button>
          )}
        </div>

        {downloadingAll && (
          <div className="mb-6 p-3 rounded-lg border flex items-center gap-3"
            style={{ borderColor: brand.accentBorder, background: brand.accentSoft }}>
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: brand.accent, borderTopColor: 'transparent' }} />
            <div className="flex-1">
              <div className="text-sm" style={{ color: brand.accent }}>
                Starting downloads {downloadAllProgress.current}/{downloadAllProgress.total}…
              </div>
              <div className="h-1 bg-white/10 rounded overflow-hidden mt-1.5">
                <div className="h-full transition-all"
                  style={{ width: `${(downloadAllProgress.current / Math.max(1, downloadAllProgress.total)) * 100}%`, background: brand.accent }} />
              </div>
            </div>
          </div>
        )}

        <FindMyPhotosBanner transferRef={token} className="mb-4" />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="file-grid">
          {orderedFiles.map((f, idx) => {
            const downloaded = downloadedIds.has(f.drive_file_id);
            const clickable = isPreviewable(f.mime_type);
            const section = sectionStarts.get(idx);
            return (
              <React.Fragment key={f.drive_file_id}>
              {section && (
                <div className={`col-span-full flex flex-wrap items-end justify-between gap-2 ${idx === 0 ? '' : 'mt-4 pt-4 border-t border-white/10'}`} data-testid={`day-header-${section.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-base sm:text-lg truncate" style={{ color: section.status === 'uploading' ? brand.accent : undefined }}>{section.title}</div>
                    <div className="text-xs text-white/50">
                      {section.files.length} {section.files.length === 1 ? 'file' : 'files'} · {fmtSize(section.files.reduce((a, x) => a + (x.size || 0), 0))}
                      {section.status === 'uploading' ? ' · still uploading — this page refreshes automatically' : ''}
                    </div>
                  </div>
                  <button onClick={() => downloadDay(section)} disabled={downloadingAll}
                    className="px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 border border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-50"
                    data-testid={`download-day-${section.id}`}>
                    <Download className="w-3.5 h-3.5" /> Download {section.title.split(' · ')[0]} ({section.files.length})
                  </button>
                </div>
              )}
              <div
                className="group rounded-lg overflow-hidden bg-white/[0.03] border border-white/10 transition-colors"
                style={{ borderColor: undefined }}
                data-testid={`file-card-${f.drive_file_id}`}>
                <button
                  type="button"
                  onClick={() => openLightbox(f, idx)}
                  className={`aspect-square w-full bg-black/40 relative overflow-hidden block ${clickable ? 'cursor-zoom-in' : 'cursor-default'}`}
                  data-testid={`open-lightbox-${f.drive_file_id}`}>
                  <Thumb file={f} isLocal={isLocal} />
                  {downloaded && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-black" />
                    </div>
                  )}
                  {isVideo(f.mime_type) && (
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-black/60 rounded">Video</div>
                  )}
                </button>
                <div className="p-2 sm:p-2.5">
                  <div className="text-xs font-medium text-white truncate" title={f.name}>{f.name}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">{fmtSize(f.size)}</div>
                  <button onClick={() => openDownload(f)}
                    className="w-full mt-2 py-1.5 rounded bg-white/5 hover:opacity-90 text-xs font-medium transition-colors flex items-center justify-center gap-1"
                    onMouseEnter={(e) => { e.currentTarget.style.background = brand.accent; e.currentTarget.style.color = brand.accentText; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.color = ''; }}
                    data-testid={`download-file-${f.drive_file_id}`}>
                    <Download className="w-3.5 h-3.5" />
                    {downloaded ? 'Download again' : 'Download'}
                  </button>
                </div>
              </div>
              </React.Fragment>
            );
          })}
        </div>

        {emptyDays.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="empty-days">
            {emptyDays.map((s) => (
              <div key={s.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02] text-sm flex flex-wrap items-center gap-2">
                <Clock className={`w-4 h-4 ${s.status === 'uploading' ? 'animate-pulse' : ''}`} style={{ color: brand.accent }} />
                <span className="font-medium">{s.title}</span>
                <span className="text-white/50 text-xs">{s.status === 'uploading' ? '— files are being uploaded right now; they will appear here automatically' : '— no files yet'}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 sm:mt-12 pt-6 border-t border-white/5 text-xs text-white/40 flex flex-wrap items-center gap-2">
          <span>Powered by</span>
          <span className="font-semibold" style={{ color: brand.accent }} data-testid="brand-footer">{brand.name}</span>
          <span className="text-white/20">·</span>
          <span>Files are stored securely and will be permanently deleted after the expiry date.</span>
        </div>
      </main>

      {lightboxIdx !== null && (
        <Lightbox
          files={orderedFiles.filter((f) => isPreviewable(f.mime_type))}
          startIndex={Math.max(0, orderedFiles.filter((f) => isPreviewable(f.mime_type)).findIndex((f) => f.drive_file_id === orderedFiles[lightboxIdx]?.drive_file_id))}
          onClose={() => setLightboxIdx(null)}
          onDownload={openDownload}
          isLocal={isLocal}
          brand={brand}
        />
      )}
    </div>
  );
};

export default TransferDownloadPage;
